import {
	Box,
	Typography,
	Stack,
	Button,
	Accordion,
	AccordionSummary,
	AccordionDetails,
} from "@mui/material";
import { User, Loader, ChevronDown, ArrowRight } from "lucide-react";
import "../assignNurseModal.scss";
import { useContext, useEffect, useReducer, useRef, useState } from "react";
import { OptionCategory, OptionRange } from "../../../interfaces/OptionCategory";
import variables from "../../../styles/variables.module.scss";
import { ReactComponent as AccesoVascular } from "../../../assets/icons/patients/NTISS/Acceso vascular.svg";
import { ReactComponent as Cardiovascular } from "../../../assets/icons/patients/NTISS/Cardiovascular.svg";
import { ReactComponent as Medicamentos } from "../../../assets/icons/patients/NTISS/Medicamentos.svg";
import { ReactComponent as Metabólico } from "../../../assets/icons/patients/NTISS/Metabólico-Nutrición.svg";
import { ReactComponent as Monitoreo } from "../../../assets/icons/patients/NTISS/Monitoreo.svg";
import { ReactComponent as Procedimientos } from "../../../assets/icons/patients/NTISS/Procedimientos.svg";
import { ReactComponent as Respiratorio } from "../../../assets/icons/patients/NTISS/Respiratorio.svg";
import { ReactComponent as Transfusiones } from "../../../assets/icons/patients/NTISS/Transfusiones.svg";
import { categoryReducer } from "../categoryReducer";
import { getHeaders, URI_API } from "../../../utils/config";
import { PatientsListContext } from "../../../components/PatientsList/PatientsList";

interface ModalNTISSBodyProps {
	handleTab: () => void;
	setIsEdit: (value: boolean) => void;
	activeItems: any;
	categoryState: any;
	dispatchCategory: any;
}

export default function ModalNTISSBody({
	handleTab,
	setIsEdit,
	activeItems,
	categoryState,
	dispatchCategory,
}: ModalNTISSBodyProps) {
	const { selectedPatient, setSelectedPatient, setCategoryList } =
		useContext(PatientsListContext);
	const [isDisabled, setIsDisabled] = useState(true);
	const [isLoading, setIsLoading] = useState(true);
	const [ntissInformation, setNtissInformation] = useState<Map<string, OptionCategory[]>>();
	const [ntissRange, setNtissRanges] = useState<OptionRange[]>([]);
	const buttonRefs = useRef<Array<Array<HTMLButtonElement | null>>>([]);
	const accordionRefs = useRef<Array<HTMLDivElement | null>>([]);
	const [activeAccordionIndex, setActiveAccordionIndex] = useState<number | null>(0);
	const [focusedIndex, setFocusedIndex] = useState<number | null>(null);

	//Lógica para obtener la info de la DB

	const getNTISSInformation = async () => {
        setIsLoading(true);
        try {
            // ÚNICA LLAMADA AL BACKEND
            // Le pasamos ?type=NTISS para que el backend sepa qué tablas buscar
            const response = await fetch(
                `${URI_API}/patient_checklist_init/${selectedPatient?.id}?type=NTISS`, 
                getHeaders()
            );

            const responseBody = await response.json();
            if (!response.ok) {
                console.error("Error del backend:", responseBody);
                setIsLoading(false);
                return;
            }

            const { 
                active_items = [], 
                ranges = [], 
                checklist = [] 
            } = responseBody;

            // 1. Procesar Ranges
            setNtissRanges(
                ranges.map((r: any) => ({
                    id: r.id,
                    description: r.description,
                    category: r.category,
                    min: parseFloat(r.min),
                    max: parseFloat(r.max),
                })).sort((a: OptionRange, b: OptionRange) => a.min - b.min)
            );

            // 2. Procesar Checklist y Active Items (Merge)
            // Convertimos active_items a un Set para búsqueda rápida O(1)
            const activeIds = new Set(active_items.map((i: any) => i.id));

            const processedCategories = checklist.map((c: any) => ({
                id: c.id,
                category: c.category,
                subcategory: c.subcategory,
                description: c.description,
                value: c.value,
                checked: activeIds.has(c.id), // Aquí hacemos el cruce directo
            }));

            // Actualizamos Contexto y Reducer
            setCategoryList(processedCategories);
            dispatchCategory({
                type: "INITIALIZE_OPTIONS",
                payload: {
                    categories: processedCategories.map((cat: any) => ({
                        id: cat.id,
                        value: cat.value,
                        checked: cat.checked,
                    })),
                },
            });

            // 3. Procesar Mapa para el Acordeón (Tu lógica visual existente)
            let ntiss_map = new Map<string, OptionCategory[]>();
            for (let ntiss of processedCategories) {
                // ... (Tu lógica de switch case para poner títulos queda IGUAL) ...
                // Solo asegúrate de usar 'ntiss' que viene del loop processedCategories
                let option = { ...ntiss, title: "" }; 
                
                switch (option.subcategory) {
					case "respiratory":
						option.title = "Respiratorio";
						break;
					case "monitory":
						option.title = "Monitoreo";
						break;
					case "cardio":
						option.title = "Cardiovascular";
						break;
					case "vascular_access":
						option.title = "Acceso Vascular";
						break;
					case "nutrition":
						option.title = "Metabólico/Nutrición";
						break;
					case "transfusions":
						option.title = "Transfusiones";
						break;
					case "procedures":
						option.title = "Procedimientos";
						break;
					case "medicine":
						option.title = "Medicamentos";
						break;
				}

				if (ntiss_map.has(option.subcategory)) {
					let currentOptions: OptionCategory[] = ntiss_map.get(option.subcategory)!;
					currentOptions.push(option);

					ntiss_map.set(option.subcategory, currentOptions);
				} else {
					ntiss_map.set(option.subcategory, [option]);
				}
            }

            if (selectedPatient?.category_id === null) {
                setIsEdit(true);
            }
            setNtissInformation(ntiss_map);

        } catch (error) {
            console.error("Error cargando NTISS", error);
        } finally {
            setIsLoading(false);
        }
    };

	// const getNTISSInformation = async () => {
	// 	// Get NTISS information.
	// 	let ntiss_res = await (await fetch(`${URI_API}/checklist/NTISS`, getHeaders())).json();

	// 	// Get NTISS range.
	// 	// Two ranges: [0 - 63, 64 - 127]
	// 	let ntiss_ranges_res = await (
	// 		await fetch(`${URI_API}/class_ranges/patient_ntiss`, getHeaders())
	// 	).json();

	// 	setNtissRanges(
	// 		ntiss_ranges_res
	// 			.map((ntiss_range: any) => {
	// 				return {
	// 					id: ntiss_range.id,
	// 					description: ntiss_range.description,
	// 					category: ntiss_range.category,
	// 					min: parseFloat(ntiss_range.minimum_value),
	// 					max: parseFloat(ntiss_range.maximum_value),
	// 				};
	// 			})
	// 			.sort((a: OptionRange, b: OptionRange) => a.min - b.min),
	// 	);

	// 	const categories = ntiss_res.map((category: any) => {
	// 		return {
	// 			id: category.id,
	// 			category: category.category,
	// 			subcategory: category.subcategory,
	// 			description: category.description,
	// 			value: category.value,
	// 			checked: false,
	// 		};
	// 	});

	// 	setCategoryList(categories);

	// 	let updatedCategories = categories;

	// 	if (activeItems?.data?.length > 0) {
	// 		const activeCategoryIds = new Set(activeItems.data.map((item: any) => item.id));

	// 		updatedCategories = categories.map((cat: any) => ({
	// 			...cat,
	// 			checked: activeCategoryIds.has(cat.id),
	// 		}));

	// 		dispatchCategory({
	// 			type: "INITIALIZE_OPTIONS",
	// 			payload: {
	// 				categories: updatedCategories.map((cat: any) => ({
	// 					id: cat.id,
	// 					value: cat.value,
	// 					checked: cat.checked,
	// 				})),
	// 			},
	// 		});

	// 		setIsLoading(false);
	// 	}

	// 	let ntiss_map = new Map<string, OptionCategory[]>();

	// 	for (let ntiss of ntiss_res) {
	// 		let option: OptionCategory = {
	// 			category: ntiss.category,
	// 			id: ntiss.id,
	// 			value: ntiss.value,
	// 			description: ntiss.description,
	// 			subcategory: ntiss.subcategory,
	// 			title: "",
	// 			checked: null,
	// 		};

	// 		switch (option.subcategory) {
	// 			case "respiratory":
	// 				option.title = "Respiratorio";
	// 				break;
	// 			case "monitory":
	// 				option.title = "Monitoreo";
	// 				break;
	// 			case "cardio":
	// 				option.title = "Cardiovascular";
	// 				break;
	// 			case "vascular_access":
	// 				option.title = "Acceso Vascular";
	// 				break;
	// 			case "nutrition":
	// 				option.title = "Metabólico/Nutrición";
	// 				break;
	// 			case "transfusions":
	// 				option.title = "Transfusiones";
	// 				break;
	// 			case "procedures":
	// 				option.title = "Procedimientos";
	// 				break;
	// 			case "medicine":
	// 				option.title = "Medicamentos";
	// 				break;
	// 		}

	// 		if (ntiss_map.has(option.subcategory)) {
	// 			let currentOptions: OptionCategory[] = ntiss_map.get(option.subcategory)!;
	// 			currentOptions.push(option);

	// 			ntiss_map.set(option.subcategory, currentOptions);
	// 		} else {
	// 			ntiss_map.set(option.subcategory, [option]);
	// 		}
	// 	}

	// 	if (selectedPatient?.category_id === null) {
	// 		setIsEdit(true);
	// 	}

	// 	setNtissInformation(ntiss_map);
	// 	if (activeItems?.data?.length === 0) setIsLoading(false);
	// };

	useEffect(() => {
		getNTISSInformation();
	}, []);

	//Lógica para establecer características de renderizado

	const SubcategoryIcon = ({ subcategory }: { subcategory: string }) => {
		let icon = <Box></Box>;
		switch (subcategory) {
			case "respiratory":
				icon = <Respiratorio className="icons-large" />;
				break;
			case "monitory":
				icon = <Monitoreo className="icons-small" />;
				break;
			case "cardio":
				icon = <Cardiovascular className="icons-small" />;
				break;
			case "vascular_access":
				icon = <AccesoVascular className="icons-large" />;
				break;
			case "nutrition":
				icon = <Metabólico className="icons-small" />;
				break;
			case "transfusions":
				icon = <Transfusiones className="icons-small" />;
				break;
			case "procedures":
				icon = <Procedimientos className="icons-medium" />;
				break;
			case "medicine":
				icon = <Medicamentos className="icons-medium" />;
				break;
		}
		return (
			<>
				<Box>{icon}</Box>
			</>
		);
	};

	const SubcategoryClassName = ({ subcategory }: { subcategory: string }) => {
		let className = "";
		switch (subcategory) {
			case "respiratory":
				className = "tiss-sections-purple-section";
				break;
			case "transfusions":
				className = "tiss-sections-light-blue-section";
				break;
			case "nutrition":
				className = "tiss-sections-rose-section";
				break;
			case "vascular_access":
				className = "tiss-sections-yellow-section";
				break;
			case "cardio":
				className = "tiss-sections-green-section";
				break;
			case "monitory":
				className = "tiss-sections-blue-section";
				break;
			case "procedures":
				className = "tiss-sections-purple-section";
				break;
			case "medicine":
				className = "tiss-sections-blue-section";
				break;
			default:
				className = "stack-item-container";
		}
		return className;
	};

	const subcategoryColors: { [key: string]: string } = {
		respiratory: variables.primary800,
		monitory: variables.blue600,
		cardio: variables.green500,
		vascular_access: variables.yellow500,
		nutrition: variables.rose500,
		transfusions: variables.blue400,
		procedures: variables.primary800,
		medicine: variables.blue600,
	};

	const getSubcategoryColor = (subcategory: string): string =>
		subcategoryColors[subcategory] || variables.primary800;

	//Lógica para seleccionar botones y activary desactivar el guardado

	useEffect(() => {
		setCategoryList((prev: any) =>
			prev.map((item: any) => ({
				...item,
				checked: categoryState.selectedOptionsCategory.includes(item.id),
			})),
		);
	}, [categoryState.selectedOptionsCategory]);

	useEffect(() => {
		setIsDisabled(categoryState.selectedOptionsCategory.length === 0);
	}, [categoryState.selectedOptionsCategory]);

	//Lógica para el navegado con teclado

	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			if (!ntissInformation) return;

			const length = Array.from(ntissInformation).length;

			if (e.key === "Tab") {
				e.preventDefault();
				setActiveAccordionIndex((prev) => {
					const nextIndex = e.shiftKey
						? prev === null
							? length - 1
							: (prev - 1 + length) % length
						: prev === null
						? 0
						: (prev + 1) % length;
					setFocusedIndex(null);
					accordionRefs.current[nextIndex]?.scrollIntoView({
						behavior: "smooth",
						block: "start",
					});
					return nextIndex;
				});
			}

			if (activeAccordionIndex !== null) {
				const currentButtons = buttonRefs.current[activeAccordionIndex] || [];

				if (e.key === "ArrowDown") {
					e.preventDefault();
					if (focusedIndex === null) {
						setFocusedIndex(0);
					} else if (focusedIndex < currentButtons.length - 1) {
						setFocusedIndex((prev) => prev! + 1);
					} else if (activeAccordionIndex < length - 1) {
						const nextIndex = activeAccordionIndex + 1;
						const nextButtons = buttonRefs.current[nextIndex] || [];
						setActiveAccordionIndex(nextIndex);
						setFocusedIndex(nextButtons.length ? 0 : null);
						accordionRefs.current[nextIndex]?.scrollIntoView({
							behavior: "smooth",
							block: "start",
						});
					}
				} else if (e.key === "ArrowUp") {
					e.preventDefault();
					if (focusedIndex === null) {
						setFocusedIndex(currentButtons.length - 1);
					} else if (focusedIndex > 0) {
						setFocusedIndex((prev) => prev! - 1);
					} else if (activeAccordionIndex > 0) {
						const prevIndex = activeAccordionIndex - 1;
						const prevButtons = buttonRefs.current[prevIndex] || [];
						setActiveAccordionIndex(prevIndex);
						setFocusedIndex(prevButtons.length ? prevButtons.length - 1 : null);
						accordionRefs.current[prevIndex]?.scrollIntoView({
							behavior: "smooth",
							block: "start",
						});
					}
				} else if (e.key === "Enter") {
					e.preventDefault();
					if (focusedIndex !== null) {
						currentButtons[focusedIndex]?.click();
					}
				}
			}
		};

		window.addEventListener("keydown", handleKeyDown);
		return () => window.removeEventListener("keydown", handleKeyDown);
	}, [activeAccordionIndex, focusedIndex, ntissInformation]);

	useEffect(() => {
		if (
			activeAccordionIndex !== null &&
			focusedIndex !== null &&
			buttonRefs.current[activeAccordionIndex]?.[focusedIndex]
		) {
			buttonRefs.current[activeAccordionIndex][focusedIndex]?.focus();
		}
	}, [focusedIndex, activeAccordionIndex]);

	//Función que renderiza las opciones

	const buildOptions = () => {
		if (ntissInformation === undefined) return [];

		return Array.from(ntissInformation).map(
			(value: [string, OptionCategory[]], accordionIndex) => {
				buttonRefs.current[accordionIndex] = [];

				return (
					<Box
						ref={(el) => {
							accordionRefs.current[accordionIndex] = el as HTMLDivElement | null;
						}}
					>
						<Accordion
							className="tiss-sections-section-content"
							defaultExpanded
						>
							<AccordionSummary
								className="tiss-sections-section-content-section-title"
								sx={{
									backgroundColor: getSubcategoryColor(value[1][0].subcategory),
								}}
								expandIcon={
									<ChevronDown className="tiss-sections-section-content-section-title-expand-icon" />
								}
								key={accordionIndex}
							>
								<SubcategoryIcon subcategory={value[1][0].subcategory} />
								<Typography className="tiss-sections-section-content-section-title-title">
									{value[1][0].title}
								</Typography>
							</AccordionSummary>
							<AccordionDetails className="tiss-sections-section-content-tiss-content">
								<Stack spacing={1}>
									{value[1].map((value: OptionCategory, index: number) => {
										const colorClass = SubcategoryClassName({
											subcategory: value.subcategory,
										});
										const isSelected =
											categoryState.selectedOptionsCategory.includes(
												value.id,
											);
										const isFocused =
											focusedIndex === index &&
											activeAccordionIndex === accordionIndex;
										return (
											<Button
												key={value.id}
												tabIndex={isFocused ? 0 : -1}
												ref={(el) =>
													(buttonRefs.current[accordionIndex][index] = el)
												}
												className={
													isSelected
														? `${colorClass}-selected`
														: colorClass
												}
												onClick={(e) => {
													if (activeAccordionIndex !== accordionIndex) {
														setActiveAccordionIndex(accordionIndex);
													}

													setFocusedIndex(index);

													dispatchCategory({
														type: "TOGGLE_OPTION",
														payload: {
															id: value.id,
															value: value.value,
														},
													});

													e.currentTarget.blur();
												}}
												disableRipple
											>
												<Loader
													className={
														isSelected
															? `${colorClass}-icon-selected`
															: `${colorClass}-icon`
													}
												/>
												<Typography className="stack-item-container-text">
													{value.description}
												</Typography>
											</Button>
										);
									})}
								</Stack>
							</AccordionDetails>
						</Accordion>
					</Box>
				);
			},
		);
	};

	//Lógica para obtener la info de guardado y guardar

	const getNtissId = (): string => {
		return categoryState.count >= ntissRange[0].min && categoryState.count <= ntissRange[0].max
			? ntissRange[0].id
			: ntissRange[1].id;
	};

	const handleNext = (): void => {
		// Set CategoryId to selectedPatient

		setSelectedPatient({
			...selectedPatient,
			categoryid: getNtissId(),
			categoryname: "NTISS " + categoryState.count,
			ntiss_value: categoryState.count,
		});

		handleTab();
	};

	return (
		<Box>
			{isLoading ? (
				<Box>
					<Box className="category-loader-container">
						<Box className="category-loader-container__loader"></Box>
					</Box>
				</Box>
			) : (
				<Box>
					<Box className="modal-tab-content__patient-info">
						<Box sx={{ display: "flex" }}>
							<Typography className="modal-tab-content__patient-info__tag">
								Paciente:
							</Typography>
							<Typography className="modal-tab-content__patient-info__info">
								<User className="modal-tab-content__patient-info__info-user-icon" />
								{selectedPatient?.name}
							</Typography>
							<Typography
								sx={{ marginLeft: "30px" }}
								className="modal-tab-content__patient-info__tag"
							>
								Cama:
							</Typography>
							<Typography className="modal-tab-content__patient-info__info">
								{selectedPatient?.room}
							</Typography>
						</Box>
						<Box sx={{ display: "flex" }}>
							<Typography className="modal-tab-content__patient-info__tag">
								Puntaje NTISS:
							</Typography>
							<Typography className="modal-tab-content__patient-info__info">
								{categoryState.count}
							</Typography>
						</Box>
					</Box>

					<Box className="tiss-sections">{buildOptions()}</Box>
					<Box className="modal-footer">
						<Button
							variant="outlined"
							className="modal-footer-button"
							onClick={(e) => {
								handleNext(), e.currentTarget.blur();
							}}
							disableRipple
							disabled={isDisabled}
						>
							<Typography className="modal-footer-button-label">Siguiente</Typography>
							<ArrowRight className="modal-footer-button-icon" />
						</Button>
					</Box>
				</Box>
			)}
		</Box>
	);
}
